// helpers/prefetchPosts.ts
import { prefetchImageToBlob } from 'helpers/mediaPrefetch'
import { collectStoryPrefetchUrls } from 'helpers/storyPrefetch'
import type { MediaWithThumb } from 'types/interfaces/IFile'
import type { IPost } from 'types/interfaces/ITypes'

type PrefetchMode = 'list' | 'grid'

type PrefetchUpcomingPostsOptions = {
  mode?: PrefetchMode
  maxMediaPerPost?: number
  concurrency?: number
  alreadyPrefetched?: Set<string>
}

function safeSrc(url?: string | null): string | null {
  if (!url) return null

  const trimmed = String(url).trim()

  return trimmed || null
}

function collectUniqueSources(
  values: Array<string | null | undefined>
): string[] {
  const seen: Record<string, 1> = {}
  const out: string[] = []

  for (let i = 0; i < values.length; i++) {
    const url = safeSrc(values[i])

    if (!url || seen[url]) continue

    seen[url] = 1
    out.push(url)
  }

  return out
}

/**
 * Photos should prefetch both:
 * - Full-resolution/original image
 * - Thumbnail/preview image
 */
function collectPhotoSources(file: MediaWithThumb): string[] {
  return collectUniqueSources([
    safeSrc((file as any)?.url),
    safeSrc((file as any)?.thumb_url),
    safeSrc((file as any)?.active_thumb),
    safeSrc((file as any)?.preview?.src),
    safeSrc((file as any)?.preview_url),
    safeSrc((file as any)?.thumbnail_src),
    safeSrc((file as any)?.thumbnail)
  ])
}

/**
 * Videos should only prefetch image-based preview assets.
 *
 * Do not include file.url because it may be an HLS .m3u8 URL,
 * which prefetchImageToBlob should not treat as an image.
 */
function collectVideoSources(file: MediaWithThumb): string[] {
  return collectUniqueSources([
    safeSrc((file as any)?.active_thumb),
    safeSrc((file as any)?.thumb_url),
    safeSrc((file as any)?.preview?.src),
    safeSrc((file as any)?.preview_url),
    safeSrc((file as any)?.thumbnail_src),
    safeSrc((file as any)?.thumbnail)
  ])
}

function collectPostSources(
  post: IPost,
  maxMediaPerPost: number
): string[] {
  const out: string[] = []

  const stories = Array.isArray((post as any)?.stories)
    ? (post as any).stories
    : []

  if (stories.length > 0) {
    return collectStoryPrefetchUrls(stories)
  }

  const photos: MediaWithThumb[] =
    (post?.is_purchased
      ? (post?.photos as MediaWithThumb[])
      : (post?.photos_preview as MediaWithThumb[])) ?? []

  const videos: MediaWithThumb[] =
    (post?.is_purchased
      ? (post?.videos as MediaWithThumb[])
      : (post?.videos_preview as MediaWithThumb[])) ?? []

  let processedMedia = 0

  for (
    let i = 0;
    i < photos.length && processedMedia < maxMediaPerPost;
    i++
  ) {
    out.push(...collectPhotoSources(photos[i]))
    processedMedia++
  }

  for (
    let i = 0;
    i < videos.length && processedMedia < maxMediaPerPost;
    i++
  ) {
    out.push(...collectVideoSources(videos[i]))
    processedMedia++
  }

  const goalBackground = safeSrc((post as any)?.goal?.background_src)

  if (goalBackground) {
    out.push(goalBackground)
  }

  return collectUniqueSources(out)
}

async function runPool(
  urls: string[],
  signal: AbortSignal,
  concurrency: number,
  alreadyPrefetched?: Set<string>
): Promise<void> {
  let index = 0

  const workerCount = Math.max(
    1,
    Math.min(concurrency, urls.length)
  )

  const workers = new Array(workerCount).fill(null).map(async () => {
    while (index < urls.length && !signal.aborted) {
      const url = urls[index++]

      if (!url) continue

      if (alreadyPrefetched?.has(url)) {
        continue
      }

      try {
        await prefetchImageToBlob(url, signal)

        /*
         * Only mark it after the helper resolves.
         *
         * prefetchImageToBlob itself handles:
         * - canonical cache keys
         * - in-flight request deduplication
         * - signed URL differences
         * - failure TTL
         */
        alreadyPrefetched?.add(url)
      } catch {
        // Ignore aborted waits and failed prefetches.
      }
    }
  })

  await Promise.all(workers)
}

export async function prefetchUpcomingPosts(
  posts: IPost[],
  signal: AbortSignal,
  options: PrefetchUpcomingPostsOptions = {}
): Promise<void> {
  const {
    mode = 'list',
    maxMediaPerPost = mode === 'grid' ? 1 : 2,
    concurrency = mode === 'grid' ? 3 : 4,
    alreadyPrefetched
  } = options

  if (!Array.isArray(posts) || posts.length === 0) {
    return
  }

  if (signal.aborted) {
    return
  }

  const urls: string[] = []

  for (let i = 0; i < posts.length; i++) {
    const post = posts[i]

    if (!post) continue

    urls.push(
      ...collectPostSources(
        post,
        Math.max(1, maxMediaPerPost)
      )
    )
  }

  const seen: Record<string, 1> = {}
  const uniqueUrls: string[] = []

  for (let i = 0; i < urls.length; i++) {
    const url = urls[i]

    if (!url || seen[url]) continue
    if (alreadyPrefetched?.has(url)) continue

    seen[url] = 1
    uniqueUrls.push(url)
  }

  if (uniqueUrls.length === 0) {
    return
  }

  await runPool(
    uniqueUrls,
    signal,
    Math.max(1, concurrency),
    alreadyPrefetched
  )
}